import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Textarea,
  Select,
  Checkbox,
  Text,
  useToast,
  useColorModeValue,
  Box,
  Icon,
} from '@chakra-ui/react';
import { FiClipboard } from 'react-icons/fi';
import moment from 'moment';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../../contexts/AuthContext';
import { useCrmSettings } from '../../contexts/CrmSettingsContext';
import { supabase } from '../../utils/supabase';
import { functionsUsEast1 } from '../../utils/init-firebase';
import { buildDncUpdate } from '../../utils/dnc';

/**
 * Log Activity Modal (contact-level)
 *
 * Manually record an interaction (call, text, email, meeting, etc.) directly
 * on a contact — with or without a deal. Mirrors the DealPage "Log Activity"
 * pattern: writes a `type: 'disposition'` row to crm_activities with
 * metadata.disposition, which the DB audit trigger and handleAutomationTriggers
 * both understand (DISPOSITION_LOGGED campaigns, deal call-gate stamping).
 *
 * Used by ContactDetailDrawer for LOs logging referral-partner touches and
 * the internal team logging recruiting outreach on candidates, and by
 * TaskCompletionActivityPrompt to log the interaction behind a completed task.
 */
export default function LogActivityModal({
  isOpen,
  onClose,
  contact,
  deals = [],
  onLogged,
  onCreateTask,
  initialNotes = '',      // Seed the notes field on open (e.g. completed-task context)
  defaultDealId = '',     // Preselect a deal in the Related Deal dropdown
  source = 'contact_drawer', // Written to metadata.source on the activity row
}) {
  const { currentUser } = useAuth();
  const { dispositions } = useCrmSettings();
  const toast = useToast();
  const followUpBg = useColorModeValue('gray.50', 'gray.700');

  const [disposition, setDisposition] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedDealId, setSelectedDealId] = useState('');
  const [whenValue, setWhenValue] = useState('');
  const [createFollowUp, setCreateFollowUp] = useState(false);
  const [followUpTitle, setFollowUpTitle] = useState('');
  const [followUpDueDate, setFollowUpDueDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const contactName = contact
    ? `${contact.firstName || ''} ${contact.lastName || ''}`.trim()
    : '';

  // Reset the form each time the modal opens
  useEffect(() => {
    if (isOpen) {
      setDisposition('');
      setNotes(initialNotes || '');
      setSelectedDealId(defaultDealId || '');
      setWhenValue('');
      setCreateFollowUp(false);
      setFollowUpTitle(contactName ? `Follow up with ${contactName}` : 'Follow up');
      setFollowUpDueDate(moment().add(1, 'day').format('YYYY-MM-DD'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!disposition) {
      toast({
        title: 'Select a disposition',
        description: 'Pick what kind of interaction this was (call, email, text, etc.)',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (createFollowUp && (!followUpTitle.trim() || !followUpDueDate)) {
      toast({
        title: 'Follow-up task incomplete',
        description: 'Give the follow-up task a title and due date, or uncheck it.',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const dispositionLabel =
        dispositions.find((d) => d.value === disposition)?.label ||
        disposition.replace(/-/g, ' ');

      const activityData = {
        contact_id: contact.id,
        deal_id: selectedDealId || null,
        author_id: currentUser.uid,
        type: 'disposition',
        content: notes.trim() || dispositionLabel,
        metadata: {
          authorName: currentUser.displayName || currentUser.email,
          authorPhotoURL: currentUser.photoURL || '',
          disposition,
          source,
        },
      };

      // Backdating: only send created_at when the user picked a time
      if (whenValue) {
        activityData.created_at = new Date(whenValue).toISOString();
      }

      const { data: newActivity, error: activityError } = await supabase
        .from('crm_activities')
        .insert(activityData)
        .select()
        .single();

      if (activityError) throw activityError;

      // Do Not Contact disposition blocks every channel (same as DealPage)
      if (disposition === 'do-not-contact') {
        const { error: dncError } = await supabase
          .from('crm_contacts')
          .update(buildDncUpdate({}, { everything: true }))
          .eq('id', contact.id);
        if (dncError) {
          console.error('Error marking contact DNC:', dncError);
        }
      }

      // Fire automation trigger (fire-and-forget; DB audit trigger is the backup)
      try {
        const fireTrigger = httpsCallable(functionsUsEast1, 'handleAutomationTriggers');
        fireTrigger({
          eventType: 'activity.disposition_logged',
          payload: {
            contactId: contact.id,
            dealId: selectedDealId || null,
            disposition,
          },
        });
      } catch (_) {}

      // Optional follow-up task
      if (createFollowUp && onCreateTask) {
        const [year, month, day] = followUpDueDate.split('-');
        const localDueDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 0, 0, 0);

        const taskResult = await onCreateTask({
          title: followUpTitle.trim(),
          description: notes.trim()
            ? `From logged activity (${dispositionLabel}): ${notes.trim()}`
            : `Follow-up from logged activity: ${dispositionLabel}`,
          type: 'crm',
          priority: 'medium',
          assignedTo: currentUser.uid,
          assignedBy: currentUser.uid,
          dueDate: localDueDate,
          dueTime: null,
          status: 'pending',
          isCalendarEvent: false,
          eventDuration: null,
          relatedTo: {
            type: 'contact',
            id: contact.id,
            name: contactName || 'Contact',
          },
        });

        if (taskResult && !taskResult.success) {
          toast({
            title: 'Activity logged, but follow-up task failed',
            description: taskResult.error,
            status: 'warning',
            duration: 4000,
          });
        }
      }

      toast({
        title: 'Activity logged',
        description:
          disposition === 'do-not-contact'
            ? `${dispositionLabel} — contact marked Do Not Contact`
            : dispositionLabel,
        status: 'success',
        duration: 3000,
      });

      if (onLogged) onLogged(newActivity);
      onClose();
    } catch (error) {
      console.error('Error logging activity:', error);
      toast({
        title: 'Error logging activity',
        description: error.message,
        status: 'error',
        duration: 4000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2}>
            <Icon as={FiClipboard} />
            <Text>Log Activity{contactName ? ` — ${contactName}` : ''}</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel fontSize="sm">Disposition</FormLabel>
              <Select
                value={disposition}
                onChange={(e) => setDisposition(e.target.value)}
                size="sm"
              >
                {dispositions.map((disp) => (
                  <option key={disp.value} value={disp.value}>
                    {disp.label}
                  </option>
                ))}
              </Select>
              <FormHelperText fontSize="xs">
                What kind of interaction was this? (Called, texted, emailed, met, etc.)
              </FormHelperText>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Notes</FormLabel>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="What happened? Any details worth remembering..."
                rows={3}
                size="sm"
              />
            </FormControl>

            {deals.length > 0 && (
              <FormControl>
                <FormLabel fontSize="sm">Related Deal</FormLabel>
                <Select
                  value={selectedDealId}
                  onChange={(e) => setSelectedDealId(e.target.value)}
                  size="sm"
                >
                  <option value="">Contact only (no deal)</option>
                  {deals.map((deal) => (
                    <option key={deal.id} value={deal.id}>
                      {deal.dealName || 'Unnamed Deal'}
                    </option>
                  ))}
                </Select>
                <FormHelperText fontSize="xs">
                  Optionally attach this activity to one of their deals
                </FormHelperText>
              </FormControl>
            )}

            <FormControl>
              <FormLabel fontSize="sm">When</FormLabel>
              <Input
                type="datetime-local"
                value={whenValue}
                onChange={(e) => setWhenValue(e.target.value)}
                max={moment().format('YYYY-MM-DDTHH:mm')}
                size="sm"
              />
              <FormHelperText fontSize="xs">
                Leave blank to log for right now, or backdate a past interaction
              </FormHelperText>
            </FormControl>

            {onCreateTask && (
              <Box
                p={3}
                bg={followUpBg}
                borderRadius="md"
              >
                <Checkbox
                  isChecked={createFollowUp}
                  onChange={(e) => setCreateFollowUp(e.target.checked)}
                  size="sm"
                >
                  <Text fontSize="sm" fontWeight="medium">Create follow-up task</Text>
                </Checkbox>

                {createFollowUp && (
                  <VStack spacing={3} align="stretch" mt={3}>
                    <FormControl isRequired>
                      <FormLabel fontSize="xs" mb={1}>Task Title</FormLabel>
                      <Input
                        value={followUpTitle}
                        onChange={(e) => setFollowUpTitle(e.target.value)}
                        size="sm"
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="xs" mb={1}>Due Date</FormLabel>
                      <Input
                        type="date"
                        value={followUpDueDate}
                        onChange={(e) => setFollowUpDueDate(e.target.value)}
                        min={moment().format('YYYY-MM-DD')}
                        size="sm"
                      />
                    </FormControl>
                  </VStack>
                )}
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            colorScheme="teal"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            loadingText="Logging..."
            leftIcon={<FiClipboard />}
          >
            Log Activity
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
